// src/features/feedback/hooks/useFeedback.js
import { useState, useCallback } from 'react';
import { feedbackService } from '../services/feedbackService';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import i18n from '../../../i18n';

export function useFeedback() {
  const [myFeedback, setMyFeedback] = useState([]);
  const [publishedFeedback, setPublishedFeedback] = useState([]);
  const [departmentFeedback, setDepartmentFeedback] = useState([]);
  const [feedbackDetails, setFeedbackDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  const { showSuccess, showError } = useSnackbar();

  /**
   * Submit new feedback
   */
  const submitFeedback = useCallback(async (data) => {
    setSubmitting(true);
    setError(null);

    try {
      const response = await feedbackService.submitFeedback(data);
      showSuccess(i18n.t('feedback:messages.submit_success'));
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to submit feedback';
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSubmitting(false);
    }
  }, [showSuccess, showError]);

  /**
   * Fetch user's own feedback
   */
  const fetchMyFeedback = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await feedbackService.getMyFeedback(options);
      setMyFeedback(response.data.feedback_items);
      setTotalCount(response.data.total_count);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch your feedback';
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showError]);

  /**
   * Fetch published feedback
   */
  const fetchPublishedFeedback = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await feedbackService.getPublishedFeedback(options);
      setPublishedFeedback(response.data.feedback_items);
      setTotalCount(response.data.total_count);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch published feedback';
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showError]);

  /**
   * Fetch department feedback
   */
  const fetchDepartmentFeedback = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await feedbackService.getDepartmentFeedback(options);
      setDepartmentFeedback(response.data.feedback_items);
      setTotalCount(response.data.total_count);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch department feedback';
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showError]);

  /**
   * Fetch specific feedback details
   */
  const fetchFeedbackDetails = useCallback(async (feedbackId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await feedbackService.getFeedbackDetails(feedbackId);
      setFeedbackDetails(response.data);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch feedback details';
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showError]);

  /**
   * Clear feedback details
   */
  const clearFeedbackDetails = useCallback(() => {
    setFeedbackDetails(null);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    myFeedback,
    publishedFeedback,
    departmentFeedback,
    feedbackDetails,
    loading,
    submitting,
    error,
    totalCount,

    // Actions
    submitFeedback,
    fetchMyFeedback,
    fetchPublishedFeedback,
    fetchDepartmentFeedback,
    fetchFeedbackDetails,
    clearFeedbackDetails,
    clearError,
  };
}

/**
 * Simplified hook for submitting feedback only
 */
export function useFeedbackSubmit() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { showSuccess, showError } = useSnackbar();

  const submitFeedback = useCallback(async (data) => {
    setSubmitting(true);
    setError(null);

    try {
      const response = await feedbackService.submitFeedback(data);
      showSuccess(i18n.t('feedback:messages.submit_success'));
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to submit feedback';
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSubmitting(false);
    }
  }, [showSuccess, showError]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    submitting,
    error,
    submitFeedback,
    clearError,
  };
}